import * as Dfns from 'date-fns/fp';
import { BuyableSpotRow } from './run-view-pivots';

// Day/day-range -> fixed-width bitmask string (one 2-char slot per weekday, M through Su) used by
// the downstream buying system's day-of-week column. Exact positions matter -- this isn't derived,
// it's a literal lookup table.
const DOW_FOR_DAY: Record<string, string> = {
  M: 'M----------',
  Tu: '-Tu--------',
  W: '---W-------',
  Th: '----Th-----',
  F: '------F----',
  Sa: '-------Sa--',
  Su: '---------Su',
  'M - Tu': 'MTu--------',
  'M - W': 'MTuW-------',
  'M - Th': 'MTuWTh-----',
  'M - F': 'MTuWThF----',
  'M - Sa': 'MTuWThFSa--',
  'M - Su': 'MTuWThFSaSu',
  'Tu - W': '-TuW-------',
  'Tu - Th': '-TuWTh-----',
  'Tu - F': '-TuWThF----',
  'Tu - Sa': '-TuWThFSa--',
  'Tu - Su': '-TuWThFSaSu',
  'W - Th': '---WTh-----',
  'W - F': '---WThF----',
  'W - Sa': '---WThFSa--',
  'W - Su': '---WThFSaSu',
  'Th - F': '----ThF----',
  'Th - Sa': '----ThFSa--',
  'Th - Su': '----ThFSaSu',
  'F - Sa': '------FSa--',
  'F - Su': '------FSaSu',
  'Sa - Su': '-------SaSu'
};

// Scales a map of discrete counts to percentages that always sum to exactly 100, using the
// largest-remainder method: scale each value proportionally, floor everything, then hand out the
// leftover percentage points to whichever entries had the largest fractional remainder.
export const countsToPercentages = (counts: Record<string, number>): Record<string, number> => {
  const entries = Object.entries(counts);
  const denom = entries.reduce((sum, [, value]) => sum + value, 0);
  if (denom === 0) {
    return Object.fromEntries(entries.map(([key]) => [key, 0]));
  }

  const scaled = entries.map(([key, value]) => [key, (value / denom) * 100] as [string, number]);
  const flooredSum = scaled.reduce((sum, [, value]) => sum + Math.floor(value), 0);
  const remainingPoints = 100 - flooredSum;

  // Ascending by (floor(value) - value) == descending by fractional remainder -- largest
  // remainder gets a leftover point first.
  const byRemainderDescending = [...scaled].sort(
    ([, a], [, b]) => Math.floor(a) - a - (Math.floor(b) - b)
  );
  const bumped = new Map(
    byRemainderDescending.map(([key, value], index) => [
      key,
      index < remainingPoints ? Math.floor(value) + 1 : Math.floor(value)
    ])
  );

  return Object.fromEntries(entries.map(([key]) => [key, bumped.get(key) ?? 0]));
};

// Fields that already have their own dedicated CSV column -- everything else on a buyable-spots
// row is a per-creative spot count and gets converted to a percentage column instead.
const NON_CREATIVE_KEYS = new Set([
  'avail',
  'cost',
  'Creative',
  'day',
  'daypart',
  'daypartName',
  'network',
  'Number of Spots',
  'Rotation ID',
  'secured',
  'spotLength',
  'totalSpend',
  'totalSpots'
]);

export interface ExportOptions {
  trimMornings: boolean;
  trimFridays: boolean;
  roundOdd15s: boolean;
  roundOddLocals: boolean;
  roundOddAll: boolean;
}

// Only applied outside a fixed set of network/avail carve-outs (CNBC never trims; FRFM's local
// avail never trims) and only to Daytime/Weekend dayparts -- morning inventory on other networks
// starts too early to actually be buyable, so the effective start time gets pulled forward.
const trimMorningDaypart = (row: BuyableSpotRow): string => {
  if (row.network === 'CNBC' || (row.network === 'FRFM' && row.avail === 'L')) {
    return row.daypart;
  }
  if (!row.daypartName.includes('Daytime') && !row.daypartName.includes('Weekend')) {
    return row.daypart;
  }

  const [rawStart, rawEnd] = row.daypart.split('-');
  if (!rawStart || !rawEnd) return row.daypart;

  const start = Dfns.parse(new Date(), 'hh:mma')(rawStart.trim());
  const end = Dfns.parse(new Date(), 'hh:mma')(rawEnd.trim());
  if (Dfns.getHours(start) >= 10) return row.daypart;

  let newStart: string;
  if (Dfns.getHours(end) >= 12) {
    newStart = '10:00AM';
  } else if (Dfns.getHours(end) === 11) {
    newStart = '9:00AM';
  } else {
    return row.daypart;
  }
  return `${newStart} - ${rawEnd.trim()}`;
};

const roundedTotalSpots = (row: BuyableSpotRow, options: ExportOptions): number => {
  let { totalSpots } = row;
  if (totalSpots === 1) {
    totalSpots = 2;
  }
  if (options.roundOdd15s && row.spotLength === '15' && totalSpots % 2 === 1) {
    totalSpots += 1;
  }
  if (options.roundOddLocals && row.avail === 'L' && totalSpots % 2 === 1) {
    totalSpots += 1;
  }
  if (options.roundOddAll && totalSpots % 2 === 1) {
    totalSpots += 1;
  }
  return totalSpots;
};

export type CsvRow = (string | number)[];

// Builds the CSV-shaped rows (header + one row per buyable-spot group) the media-buying system
// ingests. Every column beyond the fixed set is one creative's spot-count converted to a
// percentage of that row's total spots.
export const calculateTrimmedRows = (rows: BuyableSpotRow[], options: ExportOptions): CsvRow[] => {
  const creativeKeys = Object.keys(rows[0] ?? {})
    .filter(key => !NON_CREATIVE_KEYS.has(key))
    .sort();

  const header: CsvRow = [
    'networkGroup',
    'network',
    'avail',
    'rotationName',
    'daypart12hr',
    'dow',
    'type',
    'bonus',
    'length',
    'count',
    'cost',
    'spend',
    ...creativeKeys.map(key => `creative['${key}']`),
    'notes'
  ];

  const dataRows = rows.map((row): CsvRow => {
    const creativePercentages = countsToPercentages(
      Object.fromEntries(creativeKeys.map(key => [key, Number(row[key]) || 0]))
    );

    const daypart = options.trimMornings ? trimMorningDaypart(row) : row.daypart;
    let dow = DOW_FOR_DAY[row.day] ?? '';
    if (options.trimFridays) {
      dow = dow.replace('F', '-');
    }

    const totalSpots = roundedTotalSpots(row, options);

    return [
      '',
      row.network,
      row.avail,
      `${row.daypartName} (${row.day} ${row.daypart.toLowerCase().replace(/ /g, '')})`,
      daypart.toLowerCase().replace(/ /g, ''),
      dow,
      row.secured,
      'false',
      row.spotLength,
      totalSpots,
      row.cost,
      totalSpots * row.cost,
      ...creativeKeys.map(key => creativePercentages[key]),
      ''
    ];
  });

  return [header, ...dataRows];
};
